import type { RunState } from '../sim/run';
import type { StuntEvent } from '../sim/stunts';
import { num, t } from './i18n';

const LOW_FUEL = 0.2;

function el<T extends HTMLElement = HTMLElement>(selector: string): T {
  const found = document.querySelector<T>(selector);
  if (!found) throw new Error(`Missing element ${selector}`);
  return found;
}

/** DOM HUD: distance and best, fuel gauge with next-gas-can readout, coins, stunt toasts. Reads run state only. */
export class Hud {
  private readonly distance = el('#distance-value');
  private readonly best = el('#best-value');
  private readonly fuelPlate = el('#fuel-plate');
  private readonly fuelMeter = el('#fuel-meter');
  private readonly fuelFill = el('#fuel-fill');
  private readonly nextFuel = el('#next-fuel-value');
  private readonly coins = el('#coins-value');
  private readonly toasts = el('#toasts');
  private readonly startHint = el('#start-hint');
  private readonly readyActions = el('#ready-actions');
  private readonly wallet = el('#wallet-value');
  private readonly ghostGap = el('#ghost-gap');
  private readonly tipEl = el('#tip');
  private tipTimer: number | null = null;
  private lastGap = '';
  private last = { distance: -1, best: -1, fuel: -1, nextFuel: '', coins: -1, low: false, hint: true };

  update(state: RunState, best: number, nextFuelMetres: number | null): void {
    const distance = Math.floor(state.distance);
    if (distance !== this.last.distance) this.distance.textContent = String((this.last.distance = distance));
    const shownBest = Math.floor(Math.max(best, state.distance));
    if (shownBest !== this.last.best) this.best.textContent = String((this.last.best = shownBest));

    const fraction = state.fuel / state.fuelCapacity;
    const rounded = Math.round(fraction * 200) / 200;
    if (rounded !== this.last.fuel) {
      this.last.fuel = rounded;
      this.fuelFill.style.transform = `scaleX(${rounded})`;
      this.fuelMeter.setAttribute('aria-valuenow', String(Math.round(rounded * 100)));
    }
    const low = fraction < LOW_FUEL && state.status !== 'ended';
    if (low !== this.last.low) this.fuelPlate.classList.toggle('is-low', (this.last.low = low));

    const fuelText = nextFuelMetres === null ? '—' : `${Math.max(0, Math.round(nextFuelMetres))} m`;
    if (fuelText !== this.last.nextFuel) this.nextFuel.textContent = this.last.nextFuel = fuelText;

    if (state.coins !== this.last.coins) {
      const grew = this.last.coins >= 0 && state.coins > this.last.coins;
      this.coins.textContent = String((this.last.coins = state.coins));
      if (grew) this.coins.animate([{ transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { duration: 140, easing: 'ease-out' });
    }

    const hint = state.status === 'ready';
    if (hint !== this.last.hint) {
      this.startHint.classList.toggle('is-hidden', !(this.last.hint = hint));
      this.readyActions.classList.toggle('is-hidden', !hint);
    }
  }

  /** Metres ahead (+) or behind (−) the ghost; null hides the readout. */
  setGhostGap(metres: number | null): void {
    const text = metres === null ? '' : t('ghost.gap', { gap: `${metres >= 0 ? '+' : '−'}${Math.abs(Math.round(metres))}` });
    if (text === this.lastGap) return;
    this.lastGap = text;
    this.ghostGap.hidden = metres === null;
    this.ghostGap.textContent = text;
    this.ghostGap.classList.toggle('is-behind', metres !== null && metres < 0);
  }

  setEnvironmentName(name: string): void {
    el('#map-button-label').textContent = name;
  }

  /** A first-run tip that stays long enough to read. */
  tip(text: string): void {
    if (this.tipTimer !== null) window.clearTimeout(this.tipTimer);
    this.tipEl.textContent = text;
    this.tipEl.hidden = false;
    this.tipTimer = window.setTimeout(() => {
      this.tipEl.hidden = true;
      this.tipTimer = null;
    }, 4500);
  }

  /** A general announcement toast (missions, goals). */
  notice(text: string, good = false): void {
    this.toast(text, good);
  }

  setWallet(coins: number): void {
    this.wallet.textContent = String(coins);
  }

  stunt(stunt: StuntEvent): void {
    if (stunt.type === 'flip') {
      this.toast(t(stunt.direction === 'back' ? 'toast.backflip' : 'toast.frontflip', { coins: stunt.coins }));
      this.toast(t('toast.fuel', { fuel: stunt.fuel }), true);
    } else {
      this.toast(t('toast.air', { s: num(stunt.seconds), coins: stunt.coins }));
    }
  }

  delivery(metres: number, coins: number): void {
    this.toast(t('toast.delivered', { m: metres, coins }));
  }

  fuelCollected(): void {
    this.toast(t('toast.gascan'), true);
  }

  clearToasts(): void {
    this.toasts.replaceChildren();
  }

  private toast(text: string, fuel = false): void {
    const node = document.createElement('div');
    node.className = fuel ? 'toast is-fuel' : 'toast';
    node.textContent = text;
    this.toasts.append(node);
    while (this.toasts.children.length > 3) this.toasts.firstElementChild?.remove();
    node
      .animate(
        [
          { opacity: 0, transform: 'translateY(8px) scale(0.9)' },
          { opacity: 1, transform: 'translateY(0) scale(1)', offset: 0.15 },
          { opacity: 1, offset: 0.75 },
          { opacity: 0, transform: 'translateY(-6px)' },
        ],
        { duration: 1600, easing: 'ease-out' },
      )
      .finished.then(() => node.remove())
      .catch(() => node.remove());
  }
}
